'use client'

import { useEffect, useRef, useState } from 'react'

/** Animates a numeric value up from 0 to `target` (eased) whenever it changes.
 * Snaps straight to the target under prefers-reduced-motion.
 */
export function useCountUp(target: number | undefined, durationMs = 700): number | undefined {
  const [animatedValue, setAnimatedValue] = useState<number | undefined>(target)
  const animationFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (target == null) {
      setAnimatedValue(undefined)
      return
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion) {
      setAnimatedValue(target)
      return
    }

    const startTime = performance.now()

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / durationMs, 1)
      const eased = 1 - (1 - progress) ** 3
      setAnimatedValue(Math.round(target * eased))
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(tick)
      }
    }

    animationFrameRef.current = requestAnimationFrame(tick)

    return () => {
      if (animationFrameRef.current != null) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [target, durationMs])

  return animatedValue
}
